import { Box } from '@mui/material'
import React, { useState } from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Htc320hIo } from 'src/Libs/flow/installation/forms'
import { Meter } from '../../../powermeter'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { NumberField } from '@e3dc-react/shell/Elements/InputFields'
import { useDialogActions } from '../../../components/Dialogs/hooks'

export interface Htc320hIoPairingDialogProps {
  htc320hIo: Htc320hIo
  meters: Meter[]
  submitPairingId: (id: string) => void
  onClose?: () => void
}

// TODO: test with real device
export const Htc320hIoPairingDialog: React.FC<Htc320hIoPairingDialogProps> = ({ htc320hIo, meters, submitPairingId, onClose }) => {
  const { t } = useCustomTranslation()

  const initialPairingId = Number(htc320hIo.pairingId)

  const [pairingId, setPairingId] = useState(initialPairingId)

  const isOutOfBounds = !isWithinBounds(pairingId)
  const isDuplicatePairingId = getIsDuplicatePairingId(pairingId, meters)

  const error = useErrorText(isOutOfBounds, isDuplicatePairingId)
  const isSame = initialPairingId === pairingId

  const handleSubmit = (): void => {
    submitPairingId(pairingId.toString())

    // TODO: if device exists, pair and save (replace if a previous id exists).
    // If not, just set the pairingId and handle it on installation save.
    // Reference functions
    // handlePairingSave, ConfigurationEditor:1452
    // onReplaceDevice, ConfigurationEditor:1052

    onClose?.()
  }

  const handleOnClose = (): void => {
    onClose?.()
  }

  const primaryName = htc320hIo.isNew ? t('installation.productEditor.pairingEditor.save') : t('installation.productEditor.pairingEditor.pairAndSave')
  const dialogActions = useDialogActions({
    primary: {
      name: primaryName,
      onClick: handleSubmit,
      disabled: !!error || isSame,
    },
    secondary: { onClick: handleOnClose },
  })

  return (
    <Dialog maxWidth="md" onClose={handleOnClose} title={t('installation.productEditor.pairingEditor.title.iocontroller')} actions={dialogActions}>
      <Box py={2}>
        <NumberField
          name="htc320h io pairing id"
          label={t('productEditor.pairingEditor.pairingID.htc')}
          value={pairingId}
          onChange={value => setPairingId(value || 0)}
          min={1}
          max={99}
          error={!!error}
          helperText={error}
        />
      </Box>
    </Dialog>
  )
}

// #region Helpers

const useErrorText = (isOutOfBounds: boolean, isDuplicatedPairingId: boolean): string | undefined => {
  const { t } = useCustomTranslation()

  if (isOutOfBounds) return t('installation.productEditor.pairingEditor.submeter-id-out-of-bounds')
  if (isDuplicatedPairingId) return t('installation.productEditor.pairingEditor.notAllowedId')

  return undefined
}

/**
 * Returns true if pairing id is bounded to [1, 99] (both inclusive)
 * @param pairingId The pairing id in the input
 */
const isWithinBounds = (pairingId: number): boolean => {
  return 1 <= pairingId && pairingId <= 99
}

/** Check if the same id exists in a meter device. */
const getIsDuplicatePairingId = (pairingId: number, meters: Meter[]): boolean => {
  return meters.some(meter => Number(meter.pairingId) === pairingId)
}
